// ProductDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Footer from './footer';
import "./style/ProductDetail.css";
import ProductShareButton from "./ProductShareButton";
import OrderModal from './OrderModal';

const ProductDetail = () => {
    const api = "https://mkbackend-production.up.railway.app";

    // IMPORTANT:
    // URL should now contain shopSlug instead of shopId
    // Example: /shops/my-shop/product/PRODUCT_ID
    const { shopSlug,   productSlug } = useParams();

    const navigate = useNavigate();

    const [product, setProduct] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [averageRating, setAverageRating] = useState(0);
    const [totalReviews, setTotalReviews] = useState(0);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [activeTab, setActiveTab] = useState('reviews');
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [toast, setToast] = useState(null);
    const [showcart, setShowcart] = useState(false);
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Review states
    const [userReview, setUserReview] = useState(null);
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [comment, setComment] = useState('');
    const [replyText, setReplyText] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [showReplyInput, setShowReplyInput] = useState(null);
    const [ratingDistribution, setRatingDistribution] = useState({
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0
    });

    // =========================================================
    // CART
    // IMPORTANT: cart now uses shopSlug instead of shopId
    // =========================================================

  const [cart, setCart] = useState([]);

    const [shopEmail, setShopEmail] = useState(() => {
        return localStorage.getItem(`shop-email-${shopSlug}`) || "";
    });

    // Load cart whenever shopSlug changes
    useEffect(() => {
        loadCart();
    }, [shopSlug]);

    const loadCart = () => {
        const saved = localStorage.getItem(`cart-${shopSlug}`);
        const cartData = saved ? JSON.parse(saved) : [];
        setCart(cartData);
    };

    // =========================================================
    // PRODUCT + AUTH
    // =========================================================

    useEffect(() => {
        const token = localStorage.getItem('token');

        setIsAuthenticated(!!token);

        fetchProductDetails();

        if (token) {
            checkUserReview();
        }
    }, [productSlug]);

    // =========================================================
    // TOAST
    // =========================================================

    const showToast = (message) => {
        setToast(message);

        setTimeout(() => {
            setToast(null);
        }, 3000);
    };

    // =========================================================
    // FETCH PRODUCT
    // =========================================================

    const fetchProductDetails = async () => {
        try {
            setLoading(true);

            const response = await axios.get(
                `${api}/products/${productSlug}/details`
            );

            if (response.data.success) {
                setProduct(response.data.product);

                if (response.data.stats) {
                    setAverageRating(
                        response.data.stats.averageRating || 0
                    );

                    setTotalReviews(
                        response.data.stats.totalReviews || 0
                    );
                }
            }

            await fetchReviews();

        } catch (error) {
            console.error('Error fetching product:', error);
        } finally {
            setLoading(false);
        }
    };

    // =========================================================
    // FETCH REVIEWS
    // =========================================================

    const fetchReviews = async () => {
        try {
            const response = await axios.get(
                `${api}/products/${productSlug}/reviews`
            );

            if (response.data.success) {
                setReviews(response.data.reviews || []);

                if (response.data.averageRating !== undefined) {
                    setAverageRating(response.data.averageRating);
                }

                if (response.data.totalReviews !== undefined) {
                    setTotalReviews(response.data.totalReviews);
                }

                calculateDistribution(
                    response.data.reviews || []
                );
            }

        } catch (error) {
            console.error('Error fetching reviews:', error);
            setReviews([]);
        }
    };

    // =========================================================
    // RATING DISTRIBUTION
    // =========================================================

    const calculateDistribution = (reviewsData) => {
        const dist = {
            1: 0,
            2: 0,
            3: 0,
            4: 0,
            5: 0
        };

        reviewsData.forEach((review) => {
            if (review.rating >= 1 && review.rating <= 5) {
                dist[review.rating] =
                    (dist[review.rating] || 0) + 1;
            }
        });

        setRatingDistribution(dist);
    };

    // =========================================================
    // CHECK USER REVIEW
    // =========================================================

    const checkUserReview = async () => {
        try {
            const token = localStorage.getItem('token');

            if (!token) return;

            const response = await axios.get(
                `${api}/products/${productSlug}/user-review`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (
                response.data.success &&
                response.data.hasReviewed
            ) {
                setUserReview(response.data.review);
                setRating(response.data.review.rating);
                setComment(response.data.review.comment);
            }

        } catch (error) {
            console.error(
                'Error checking user review:',
                error
            );
        }
    };

    // =========================================================
    // SUBMIT REVIEW
    // =========================================================

    const handleSubmitReview = async (e) => {
        e.preventDefault();

        if (rating === 0) {
            alert('Please select a rating');
            return;
        }

        if (!comment.trim()) {
            alert('Please write a comment');
            return;
        }

        setReviewLoading(true);

        try {
            const token = localStorage.getItem('token');

            if (!token) {
                alert('Please login to submit a review');
                setReviewLoading(false);
                return;
            }

            const url = userReview
                ? `${api}/reviews/${userReview._id}`
                : `${api}/products/${productSlug}/reviews`;

            const method = userReview ? 'put' : 'post';

            const response = await axios({
                method,
                url,
                data: {
                    rating,
                    comment: comment.trim()
                },
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.data.success) {
                alert(
                    userReview
                        ? 'Review updated successfully!'
                        : 'Review submitted successfully!'
                );

                await fetchReviews();
                await checkUserReview();

                if (!userReview) {
                    setRating(0);
                    setComment('');
                }

                setShowReviewForm(false);
            }

        } catch (error) {
            console.error(
                'Error submitting review:',
                error
            );

            alert(
                error.response?.data?.message ||
                'Error submitting review'
            );
        } finally {
            setReviewLoading(false);
        }
    };

    // =========================================================
    // DELETE REVIEW
    // =========================================================

    const handleDeleteReview = async (reviewId) => {
        if (
            !window.confirm(
                'Are you sure you want to delete your review?'
            )
        ) {
            return;
        }

        try {
            const token = localStorage.getItem('token');

            const response = await axios.delete(
                `${api}/reviews/${reviewId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                alert('Review deleted successfully');

                await fetchReviews();

                setUserReview(null);
                setComment('');
                setRating(0);
            }

        } catch (error) {
            console.error(
                'Error deleting review:',
                error
            );

            alert(
                error.response?.data?.message ||
                'Error deleting review'
            );
        }
    };

    // =========================================================
    // ADD REPLY
    // =========================================================

    const handleAddReply = async (reviewId) => {
        if (!replyText.trim()) {
            alert('Please enter a reply');
            return;
        }

        try {
            const token = localStorage.getItem('token');

            const response = await axios.post(
                `${api}/reviews/${reviewId}/replies`,
                {
                    text: replyText.trim()
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                setReplyText('');
                setShowReplyInput(null);

                await fetchReviews();

                alert('Reply added successfully!');
            }

        } catch (error) {
            console.error(
                'Error adding reply:',
                error
            );

            alert(
                error.response?.data?.message ||
                'Error adding reply'
            );
        }
    };

    // =========================================================
    // DELETE REPLY
    // =========================================================

    const handleDeleteReply = async (
        reviewId,
        replyIndex
    ) => {
        if (
            !window.confirm(
                'Are you sure you want to delete this reply?'
            )
        ) {
            return;
        }

        try {
            const token = localStorage.getItem('token');

            const response = await axios.delete(
                `${api}/reviews/${reviewId}/replies/${replyIndex}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                alert('Reply deleted successfully');

                await fetchReviews();
            }

        } catch (error) {
            console.error(
                'Error deleting reply:',
                error
            );

            alert(
                error.response?.data?.message ||
                'Error deleting reply'
            );
        }
    };

    // =========================================================
    // STAR RATING COMPONENT
    // =========================================================

    const StarRating = ({
        rating,
        onRatingChange,
        onHoverChange,
        size = 24,
        interactive = true
    }) => {
        const [hover, setHover] = useState(0);

        const handleHover = (star) => {
            if (interactive && onHoverChange) {
                onHoverChange(star);
            }

            setHover(star);
        };

        const handleLeave = () => {
            if (interactive && onHoverChange) {
                onHoverChange(0);
            }

            setHover(0);
        };

        const displayRating = hover || rating || 0;

        return (
            <div className="star-rating">
                {[1, 2, 3, 4, 5].map((star) => (
                    <span
                        key={star}
                        className={`star ${
                            star <= Math.round(displayRating)
                                ? 'active'
                                : ''
                        }`}
                        onClick={() =>
                            interactive &&
                            onRatingChange &&
                            onRatingChange(star)
                        }
                        onMouseEnter={() =>
                            handleHover(star)
                        }
                        onMouseLeave={handleLeave}
                        style={{
                            fontSize: `${size}px`,
                            cursor: interactive
                                ? 'pointer'
                                : 'default',
                            marginRight: '2px',
                            color:
                                star <=
                                Math.round(displayRating)
                                    ? '#FFD700'
                                    : '#ddd',
                            transition:
                                'color 0.2s ease'
                        }}
                    >
                        ★
                    </span>
                ))}
            </div>
        );
    };

    // =========================================================
    // RATING PERCENTAGE
    // =========================================================

    const getRatingPercentage = (count) => {
        if (totalReviews === 0) {
            return 0;
        }

        return (count / totalReviews) * 100;
    };

    // =========================================================
    // TIME AGO
    // =========================================================

    const getTimeAgo = (date) => {
        if (!date) {
            return 'Recently';
        }

        const now = new Date();
        const diff = now - new Date(date);

        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) {
            return 'Just now';
        }

        if (minutes < 60) {
            return `${minutes}m ago`;
        }

        if (hours < 24) {
            return `${hours}h ago`;
        }

        if (days < 7) {
            return `${days}d ago`;
        }

        return new Date(date).toLocaleDateString();
    };

    // =========================================================
    // CART FUNCTIONS
    // =========================================================

    const addToCart = () => {
        if (!product) {
            return;
        }

       const item = {
    _id: product._id,
    slug: product.slug,

    // MongoDB shop ID
    shopId: product.owner?._id || product.owner,

    // URL/localStorage ke liye
    shopSlug: shopSlug,

    name: product.name,
    price: product.price,
    image: product.image,
    quantity: quantity
};

        setCart((prevCart) => {
            const existingItem = prevCart.find(
                (cartItem) =>
                    cartItem._id === product._id
            );

            let updatedCart;

            if (existingItem) {
                updatedCart = prevCart.map(
                    (cartItem) =>
                        cartItem._id === product._id
                            ? {
                                ...cartItem,
                                quantity:
                                    (cartItem.quantity || 1) +
                                    quantity
                            }
                            : cartItem
                );
            } else {
                updatedCart = [
                    ...prevCart,
                    {
                        ...item,
                        quantity: quantity
                    }
                ];
            }

            // IMPORTANT: slug based cart
            localStorage.setItem(
                `cart-${shopSlug}`,
                JSON.stringify(updatedCart)
            );

            return updatedCart;
        });

        showToast(
            `✅ ${product.name} added to cart!`
        );
    };

    const toggleCart = () => {
        setShowcart(!showcart);
    };

    const removeFromCart = (itemId) => {
        setCart((prevCart) => {
            const updatedCart = prevCart.filter(
                (item) => item._id !== itemId
            );

            localStorage.setItem(
                `cart-${shopSlug}`,
                JSON.stringify(updatedCart)
            );

            return updatedCart;
        });

        showToast('Item removed from cart');
    };

    const updateCartQuantity = (
        itemId,
        newQuantity
    ) => {
        if (newQuantity < 1) {
            removeFromCart(itemId);
            return;
        }

        setCart((prevCart) => {
            const updatedCart = prevCart.map(
                (item) =>
                    item._id === itemId
                        ? {
                            ...item,
                            quantity: newQuantity
                        }
                        : item
            );

            localStorage.setItem(
                `cart-${shopSlug}`,
                JSON.stringify(updatedCart)
            );

            return updatedCart;
        });
    };

    const getTotalPrice = () => {
        return cart
            .reduce(
                (total, item) =>
                    total +
                    Number(item.price) *
                    (item.quantity || 1),
                0
            )
            .toFixed(2);
    };

    // =========================================================
    // PLACE ORDER
    // =========================================================

    const handlePlaceOrder = () => {
        if (cart.length === 0) {
            showToast('Your cart is empty!');
            return;
        }

        setShowOrderModal(true);
        setShowcart(false);
    };

    // =========================================================
    // SUBMIT ORDER
    // =========================================================

    const orderSubmit = async (customerDetails) => {
        setIsSubmitting(true);

        try {
            const productsData = cart.map(
                (item) => ({
                    productId: item._id,
                    slug: item.slug,   
                    quantity: item.quantity || 1,
                    price: item.price
                })
            );

            /*
             * Backend order route may still expect shopId.
             * We get the real MongoDB shop ID from product.owner.
             * URL/localStorage uses shopSlug.
             */
          const backendShopId =
    cart[0]?.shopId ||
    product?.owner?._id ||
    product?.owner ||
    null;

if (!backendShopId) {
    showToast('❌ Shop ID missing');
    return;
}

console.log("ORDER DATA:", {
    products: productsData,
    shopId: backendShopId,
    customerDetails
});

            const res = await axios.post(
                `${api}/order`,
                {
                    products: productsData,
                    shopId: backendShopId,
                    customerDetails: customerDetails
                }
            );

            if (res.data) {
                showToast(
                    '🎉 Order placed successfully! Check your email.'
                );

                setCart([]);

                localStorage.removeItem(
                    `cart-${shopSlug}`
                );

                setShowOrderModal(false);
            }

        } catch (error) {
            console.log(error);

            showToast(
                '❌ Error placing order. Please try again.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    // =========================================================
    // LOADING
    // =========================================================

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading product...</p>
            </div>
        );
    }

    // =========================================================
    // PRODUCT NOT FOUND
    // =========================================================

    if (!product) {
        return (
            <div className="error-container">
                <h2>Product not found</h2>

                <button
                    onClick={() => navigate(-1)}
                >
                    Go Back
                </button>
            </div>
        );
    }

    // =========================================================
    // MAIN UI
    // =========================================================

    return (
        <div className="product-detail-page">

            {/* Toast */}
            {toast && (
                <div className="toast">
                    {toast}
                </div>
            )}

            {/* Order Modal */}
            <OrderModal
                isOpen={showOrderModal}
                onClose={() =>
                    setShowOrderModal(false)
                }
                onSubmit={orderSubmit}
                totalAmount={getTotalPrice()}
                isLoading={isSubmitting}
            />

            {/* Cart Overlay */}
            <div
                className={`cart-overlay ${
                    showcart ? 'open' : ''
                }`}
                onClick={toggleCart}
            ></div>

            {/* Cart Sidebar */}
            <div
                className={`cart-sidebar ${
                    showcart ? 'open' : ''
                }`}
            >
                <div className="cart-header">
                    <h3>Shopping Cart</h3>

                    <button
                        className="cart-close"
                        onClick={toggleCart}
                    >
                        ✕
                    </button>
                </div>

                <div className="cart-items">

                    {cart.length === 0 ? (
                        <div className="cart-empty">
                            <span className="cart-empty-icon">
                                🛒
                            </span>

                            <h4>
                                Your cart is empty
                            </h4>

                            <p>
                                Start shopping to add items!
                            </p>
                        </div>
                    ) : (
                        cart.map((item, index) => (
                            <div
                                key={index}
                                className="cart-item"
                            >
                                <img
                                    src={item.image}
                                    alt={item.name}
                                />

                                <div className="cart-item-details">

                                    <div className="cart-item-name">
                                        {item.name}
                                    </div>

                                    <div className="cart-item-price">
                                        Rs: {item.price}
                                    </div>

                                    <div className="cart-item-quantity">

                                        <button
                                            onClick={() =>
                                                updateCartQuantity(
                                                    item._id,
                                                    (item.quantity || 1) - 1
                                                )
                                            }
                                        >
                                            −
                                        </button>

                                        <span>
                                            {item.quantity || 1}
                                        </span>

                                        <button
                                            onClick={() =>
                                                updateCartQuantity(
                                                    item._id,
                                                    (item.quantity || 1) + 1
                                                )
                                            }
                                        >
                                            +
                                        </button>

                                    </div>
                                </div>

                                <button
                                    className="cart-item-remove"
                                    onClick={() =>
                                        removeFromCart(
                                            item._id
                                        )
                                    }
                                >
                                    ✕
                                </button>
                            </div>
                        ))
                    )}

                </div>

                {cart.length > 0 && (
                    <div className="cart-footer">

                        <div className="cart-total">
                            <span>Total:</span>

                            <span>
                                Rs: {getTotalPrice()}
                            </span>
                        </div>

                        <button
                            className="cart-checkout-btn"
                            onClick={handlePlaceOrder}
                        >
                            🛒 Proceed to Checkout
                        </button>

                    </div>
                )}
            </div>

            {/* Navigation Bar */}
            <div className="navBar">

                <h2>MK Shop</h2>

                <div
                    className="nav-actions"
                    style={{
                        display: "flex",
                        gap: "5px"
                    }}
                >

                    <button
                        onClick={toggleCart}
                        className="cart-btn"
                    >
                        🛒 Cart

                        <span className="cart-badge">
                            {cart.length}
                        </span>
                    </button>

                </div>
            </div>

            {/* Visit Shop Button */}
            <button
                className="visit-shop-btn"
                onClick={() =>
                    navigate(`/${shopSlug}`)
                }
            >
                🏪 Visit Shop
            </button>

            {/* Product Main Section */}
            <div className="product-main-section">

                {/* Product Image */}
                <div className="product-image-section">

                    <img
                        src={product.image}
                        alt={product.name}
                        className="product-main-image"
                    />

                </div>

                {/* Product Info */}
                <div className="product-info-section">

                    <h1 className="product-title">
                        {product.name}
                    </h1>

                    {/* Rating */}
                    <div className="product-rating-summary">

                        <StarRating
                            rating={Math.round(
                                averageRating
                            )}
                            size={24}
                            interactive={false}
                        />

                        <span className="rating-value">
                            {averageRating.toFixed(1)}
                        </span>

                        <span className="rating-count">
                            ({totalReviews} Reviews)
                        </span>

                    </div>

                    {/* Price */}
                    <div className="product-price-section">

                        <span className="product-price">
                            Rs: {product.price}
                        </span>

                        <span className="product-price-original">
                            Rs:{" "}
                            {(
                                Number(product.price) * 1.3
                            ).toFixed(2)}
                        </span>

                    </div>

                    {/* Description */}
                    <div className="product-description">

                        <p>
                            {product.description ||
                                'No description available'}
                        </p>

                    </div>

                    {/* Share */}
                    <div className="product-actions">

                     <ProductShareButton
    shopSlug={shopSlug}
    productSlug={productSlug}
    productName={product.name}
    shopName={product.owner?.shopName || 'Shop'}
/>
                    </div>

                    {/* Quantity + Cart */}
                    <div className="product-actions">

                        <div className="quantity-selector">

                            <button
                                onClick={() =>
                                    setQuantity(
                                        Math.max(
                                            1,
                                            quantity - 1
                                        )
                                    )
                                }
                            >
                                −
                            </button>

                            <span>
                                {quantity}
                            </span>

                            <button
                                onClick={() =>
                                    setQuantity(
                                        quantity + 1
                                    )
                                }
                            >
                                +
                            </button>

                        </div>

                        <button
                            className="btn-add-to-cart"
                            onClick={addToCart}
                        >
                            ADD TO CART
                        </button>

                    </div>

                </div>
            </div>

            {/* Tabs */}
            <div className="product-tabs">

                <button
                    className={`tab-btn ${
                        activeTab === 'reviews'
                            ? 'active'
                            : ''
                    }`}
                    onClick={() =>
                        setActiveTab('reviews')
                    }
                >
                    Reviews ({totalReviews})
                </button>

            </div>

            {/* Tab Content */}
            <div className="tab-content">

                {activeTab === 'reviews' && (
                    <div className="reviews-container-full">

                        {/* Rating Overview */}
                        <div className="rating-overview">

                            <div className="rating-big-number">

                                <div className="big-number">
                                    {averageRating.toFixed(1)}
                                </div>

                                <StarRating
                                    rating={Math.round(
                                        averageRating
                                    )}
                                    size={28}
                                    interactive={false}
                                />

                                <div className="total-reviews-text">
                                    Based on {totalReviews} reviews
                                </div>

                            </div>

                            <div className="rating-bars-full">

                                {[5, 4, 3, 2, 1].map(
                                    (star) => (
                                        <div
                                            key={star}
                                            className="rating-bar-row"
                                        >

                                            <span className="bar-label">
                                                {star} ★
                                            </span>

                                            <div className="bar-track">

                                                <div
                                                    className="bar-fill"
                                                    style={{
                                                        width: `${getRatingPercentage(
                                                            ratingDistribution[
                                                                star
                                                            ] || 0
                                                        )}%`,
                                                        background:
                                                            '#FFD700'
                                                    }}
                                                />

                                            </div>

                                            <span className="bar-count">
                                                {ratingDistribution[
                                                    star
                                                ] || 0}
                                            </span>

                                        </div>
                                    )
                                )}

                            </div>

                        </div>

                        {/* Write Review */}
                        <div className="write-review-button">

                            {isAuthenticated ? (
                                <button
                                    className="btn-write-review"
                                    onClick={() =>
                                        setShowReviewForm(
                                            !showReviewForm
                                        )
                                    }
                                >
                                    {showReviewForm
                                        ? 'Cancel'
                                        : '✏️ Write a Review'}
                                </button>
                            ) : (
                                <p className="login-prompt">
                                    Please{" "}
                                    <Link to="/login">
                                        login
                                    </Link>{" "}
                                    to write a review
                                </p>
                            )}

                        </div>

                        {/* Review Form */}
                        {showReviewForm &&
                            isAuthenticated && (
                                <div className="write-review-form">

                                    <h4>
                                        {userReview
                                            ? 'Edit Your Review'
                                            : 'Write a Review'}
                                    </h4>

                                    <form
                                        onSubmit={
                                            handleSubmitReview
                                        }
                                    >

                                        <div className="form-group">

                                            <label>
                                                Your Rating
                                            </label>

                                            <StarRating
                                                rating={rating}
                                                onRatingChange={
                                                    setRating
                                                }
                                                onHoverChange={
                                                    setHoverRating
                                                }
                                                size={32}
                                            />

                                        </div>

                                        <div className="form-group">

                                            <label>
                                                Your Review
                                            </label>

                                            <textarea
                                                value={
                                                    comment
                                                }
                                                onChange={(e) =>
                                                    setComment(
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Share your experience with this product..."
                                                rows="4"
                                                maxLength="500"
                                            />

                                        </div>

                                        <div className="review-form-actions">

                                            <button
                                                type="submit"
                                                className="btn-submit-review"
                                                disabled={
                                                    reviewLoading
                                                }
                                            >
                                                {reviewLoading
                                                    ? 'Submitting...'
                                                    : userReview
                                                    ? 'Update Review'
                                                    : 'Submit Review'}
                                            </button>

                                            {userReview && (
                                                <button
                                                    type="button"
                                                    className="btn-delete-review-form"
                                                    onClick={() =>
                                                        handleDeleteReview(
                                                            userReview._id
                                                        )
                                                    }
                                                >
                                                    Delete Review
                                                </button>
                                            )}

                                        </div>

                                    </form>

                                </div>
                            )}

                        {/* Reviews List */}
                        <div className="reviews-list-full">

                            {reviews.length === 0 ? (
                                <div className="no-reviews">
                                    <p>
                                        No reviews yet.
                                        Be the first to
                                        review!
                                    </p>
                                </div>
                            ) : (
                                reviews.map(
                                    (review) => (
                                        <div
                                            key={
                                                review._id
                                            }
                                            className="review-card"
                                        >

                                            {/* Review Header */}
                                            <div className="review-header">

                                                <div className="reviewer-info">

                                                    <div className="reviewer-avatar">
                                                        {review
                                                            .userName
                                                            ?.charAt(
                                                                0
                                                            )
                                                            .toUpperCase() ||
                                                            'U'}
                                                    </div>

                                                    <div>

                                                        <div className="reviewer-name">
                                                            {review.userName ||
                                                                'User'}
                                                        </div>

                                                        <div className="review-verified">
                                                            ✓ Verified Purchase
                                                        </div>

                                                    </div>

                                                </div>

                                                <div className="review-date">
                                                    {getTimeAgo(
                                                        review.createdAt
                                                    )}
                                                </div>

                                            </div>

                                            {/* Rating */}
                                            <div className="review-rating">

                                                <StarRating
                                                    rating={
                                                        review.rating
                                                    }
                                                    size={20}
                                                    interactive={
                                                        false
                                                    }
                                                />

                                            </div>

                                            {/* Review Body */}
                                            <div className="review-body">

                                                <h4>
                                                    {review.comment?.split(
                                                        '.'
                                                    )[0] ||
                                                        'Review'}
                                                </h4>

                                                <p>
                                                    {review.comment ||
                                                        'No comment'}
                                                </p>

                                            </div>

                                            {/* Replies */}
                                            {review.replies &&
                                                review.replies
                                                    .length >
                                                    0 && (
                                                    <div className="replies-section">

                                                        {review.replies.map(
                                                            (
                                                                reply,
                                                                index
                                                            ) => (
                                                                <div
                                                                    key={
                                                                        index
                                                                    }
                                                                    className="reply-item"
                                                                >

                                                                    <div className="reply-header">

                                                                        <span className="reply-name">
                                                                            {
                                                                                reply.userName
                                                                            }
                                                                        </span>

                                                                        <span className="reply-date">
                                                                            {getTimeAgo(
                                                                                reply.createdAt
                                                                            )}
                                                                        </span>

                                                                    </div>

                                                                    <p className="reply-text">
                                                                        {
                                                                            reply.text
                                                                        }
                                                                    </p>

                                                                </div>
                                                            )
                                                        )}

                                                    </div>
                                                )}

                                            {/* Reply */}
                                            {isAuthenticated && (
                                                <div className="reply-action">

                                                    <button
                                                        className="btn-reply"
                                                        onClick={() =>
                                                            setShowReplyInput(
                                                                showReplyInput ===
                                                                    review._id
                                                                    ? null
                                                                    : review._id
                                                            )
                                                        }
                                                    >
                                                        💬 Reply
                                                    </button>

                                                    {showReplyInput ===
                                                        review._id && (
                                                        <div className="reply-input">

                                                            <input
                                                                type="text"
                                                                value={
                                                                    replyText
                                                                }
                                                                onChange={(
                                                                    e
                                                                ) =>
                                                                    setReplyText(
                                                                        e
                                                                            .target
                                                                            .value
                                                                    )
                                                                }
                                                                placeholder="Write a reply..."
                                                                maxLength="300"
                                                            />

                                                            <button
                                                                onClick={() =>
                                                                    handleAddReply(
                                                                        review._id
                                                                    )
                                                                }
                                                            >
                                                                Post
                                                            </button>

                                                        </div>
                                                    )}

                                                </div>
                                            )}

                                        </div>
                                    )
                                )
                            )}

                        </div>

                    </div>
                )}

            </div>

            {/* AI Assistant */}
            <div className="ai-button-container">

                <Link
                    to="/ai-dashboard"
                    className="ai-button ai-button-tooltip"
                    aria-label="Open AI Assistant"
                >
                    <span className="ai-icon">
                        🤖
                    </span>

                    <span className="ai-text">
                        AI Assistant
                    </span>

                    <span className="ai-badge">
                        NEW
                    </span>
                </Link>

            </div>

            {/* Footer */}
            <Footer />

        </div>
    );
};

export default ProductDetail;